/**
 * Mode picker (sc-3016).
 *
 * After the player picks a cartridge in the selector, this canvas
 * overlay asks them to choose how to play:
 *
 *   NORMAL          — random seed, scores land on the all-time board
 *   DAILY CHALLENGE — today's deterministic seed, scores land on the
 *                     daily board (and the all-time board)
 *
 * Up/Down to switch, Z/Enter to confirm, Esc to back out to the selector.
 * Style matches the pause + initials overlays (amber border, palette).
 */

import type { GameModule } from '@arcraz/arcadia-types';
import type { InputSource } from './loop.js';

const WIDTH = 480;
const HEIGHT = 270;

const SCRIM = 'rgba(4, 6, 13, 0.55)';
const PANEL_BG = 'rgba(10, 4, 24, 0.78)';
const PANEL_BORDER = '#ffea00';
const TITLE = '#ffea00';
const SUBTITLE = '#7af3c8';
const ROW_DIM = 'rgba(255, 234, 0, 0.4)';
const ROW_FOCUSED = '#ffea00';
const ROW_DETAIL = '#ffffff';
const HINT = '#7af3c8';

export type Mode = 'normal' | 'daily';

interface ModeItem {
  readonly mode: Mode;
  readonly label: string;
  readonly subtitle: string;
}

const MODES: readonly ModeItem[] = [
  {
    mode: 'normal',
    label: 'NORMAL',
    subtitle: 'Random seed · classic arcade run',
  },
  {
    mode: 'daily',
    label: 'DAILY CHALLENGE',
    subtitle: "Today's seed · everyone faces identical conditions",
  },
];

export type ModeChoice = { kind: 'play'; mode: Mode } | { kind: 'back' };

/**
 * Run the mode-picker overlay until the player either picks a mode
 * or hits Escape to go back to the selector. Caller decides what to
 * do with the result.
 */
export function runModePicker(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  module_: GameModule
): Promise<ModeChoice> {
  let focusIndex = 0;
  let blink = 0;

  return new Promise<ModeChoice>((resolve) => {
    const tick = (): void => {
      const state = input.snapshot();

      if (state.justPressed.has('up') && focusIndex > 0) {
        focusIndex -= 1;
      } else if (state.justPressed.has('down') && focusIndex < MODES.length - 1) {
        focusIndex += 1;
      }

      if (state.justPressed.has('start') || state.justPressed.has('fireA')) {
        const item = MODES[focusIndex] as ModeItem;
        resolve({ kind: 'play', mode: item.mode });
        return;
      }

      if (state.justPressed.has('coin')) {
        resolve({ kind: 'back' });
        return;
      }

      blink = (blink + 1) % 60;
      render(ctx2d, module_, focusIndex, blink);
      requestAnimationFrame(tick);
    };

    tick();
  });
}

function render(
  g: CanvasRenderingContext2D,
  module_: GameModule,
  focusIndex: number,
  blink: number
): void {
  // Solid black behind the panel so we don't bleed any prior canvas
  // contents (the cartridge hasn't preloaded yet at this stage).
  g.fillStyle = '#04060d';
  g.fillRect(0, 0, WIDTH, HEIGHT);
  g.fillStyle = SCRIM;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  const panelW = 360;
  const panelH = 200;
  const panelX = WIDTH / 2 - panelW / 2;
  const panelY = HEIGHT / 2 - panelH / 2;

  g.fillStyle = PANEL_BG;
  g.fillRect(panelX, panelY, panelW, panelH);
  g.strokeStyle = PANEL_BORDER;
  g.lineWidth = 2;
  g.strokeRect(panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1);

  g.textAlign = 'center';
  g.textBaseline = 'top';

  g.fillStyle = TITLE;
  g.font = 'bold 14px ui-monospace, monospace';
  g.fillText(module_.title, WIDTH / 2, panelY + 14);

  g.font = '9px ui-monospace, monospace';
  g.fillStyle = SUBTITLE;
  g.fillText('SELECT MODE', WIDTH / 2, panelY + 36);

  // Two big choice rows.
  const rowsStartY = panelY + 70;
  const rowH = 42;
  for (let i = 0; i < MODES.length; i++) {
    const item = MODES[i] as ModeItem;
    const focused = i === focusIndex;
    const ry = rowsStartY + i * rowH;

    if (focused && blink < 40) {
      g.textAlign = 'left';
      g.fillStyle = ROW_FOCUSED;
      g.font = 'bold 14px ui-monospace, monospace';
      g.fillText('>', panelX + 30, ry + 4);
    }

    g.textAlign = 'center';
    g.fillStyle = focused ? ROW_FOCUSED : ROW_DIM;
    g.font = 'bold 13px ui-monospace, monospace';
    g.fillText(item.label, WIDTH / 2, ry);

    g.font = '8px ui-monospace, monospace';
    g.fillStyle = focused ? ROW_DETAIL : ROW_DIM;
    g.fillText(item.subtitle, WIDTH / 2, ry + 16);
  }

  g.font = '8px ui-monospace, monospace';
  g.fillStyle = HINT;
  g.textAlign = 'center';
  g.fillText('UP/DN — MODE    Z — START    ESC — BACK', WIDTH / 2, panelY + panelH + 8);

  g.textAlign = 'left';
  g.textBaseline = 'top';
}
